package nl.astraeus.midi.view.components

import kotlinx.html.*
import kotlinx.html.js.*
import nl.astraeus.komp.HtmlBuilder
import nl.astraeus.komp.Komponent
import org.w3c.dom.events.KeyboardEvent

//language=css
val dropdownCss: String = """
  .dropdown {
    position: relative;
    display: inline-block;
    width: 200px;
    height: 37px;
    white-space: nowrap;
    
    &.small {
      width: 100px;
    }
  }
  
  .dropdown-toggle {
    width: 100%;
    padding: 10px 15px;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    cursor: pointer;
    text-align: left;
    overflow: hidden;
    text-overflow: ellipsis;
    
    .placeholder {
        color: grey;
    }
  }
  
  .dropdown-menu {
    position: absolute;
    top: 100%;
    left: 0;
    right: 0;
    margin: 0;
    padding: 0;
    list-style: none;
    background: white;
    border: 1px solid #ccc;
    border-radius: 4px;
    box-shadow: 0 2px 8px rgba(0,0,0,0.15);
    display: none; /* JS controls visibility */
    z-index: 1000;
    
    li {
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }
  }
  
  .dropdown.open .dropdown-menu {
    display: block;
  }
  
  .dropdown-menu li {
    padding: 10px 15px;
    cursor: pointer;
  }
  
  .dropdown-menu li:hover, .dropdown-menu li.focused {
    background: #007bff;
    color: white;
    outline: none;
  }
  
  .dropdown-menu li.selected {
    background: #e9ecef;
    color: black;
  }
  """.trimIndent()


data class Selectable(
  val value: String,
  val label: String
)

class Dropdown(
  var selected: Selectable? = null,
  val options: List<Selectable>,
  val small: Boolean = false,
  val onSelect: (Selectable) -> Unit,
  val onHover: (Selectable?) -> Unit = {}
) : Komponent() {
  var open: Boolean = false
  var focusedIndex: Int = -1

  override fun HtmlBuilder.render() {
    div("dropdown") {
      if (open) {
        classes += "open"
      }
      if (small) {
        classes += "small"
      }
      button(classes = "dropdown-toggle") {
        type = ButtonType.button
        if (selected == null) {
          classes += "placeholder"
        }

        +(selected?.label ?: "<select value>")

        onClickFunction = { e ->
          e.preventDefault()
          open = !open
          if (open) {
            focusedIndex = options.indexOf(selected)
          }
          requestUpdate()
        }

        onKeyDownFunction = { e ->
          if (e is KeyboardEvent) {
            when (e.key) {
              "ArrowDown" -> {
                e.preventDefault()
                if (!open) {
                  open = true
                  focusedIndex = options.indexOf(selected)
                } else {
                  focusedIndex = (focusedIndex + 1).coerceAtMost(options.size - 1)
                }
                onHover(if (focusedIndex >= 0) options[focusedIndex] else null)
                requestUpdate()
              }

              "ArrowUp" -> {
                e.preventDefault()
                if (!open) {
                  open = true
                  focusedIndex = options.indexOf(selected)
                } else {
                  focusedIndex = (focusedIndex - 1).coerceAtLeast(0)
                }
                onHover(if (focusedIndex >= 0) options[focusedIndex] else null)
                requestUpdate()
              }

              "Enter", " " -> {
                e.preventDefault()
                if (open) {
                  if (focusedIndex in options.indices) {
                    val option = options[focusedIndex]
                    selected = option
                    onSelect(option)
                  }
                  open = false
                  onHover(null)
                  requestUpdate()
                } else {
                  open = true
                  focusedIndex = options.indexOf(selected)
                  requestUpdate()
                }
              }

              "Escape" -> {
                if (open) {
                  e.preventDefault()
                  open = false
                  onHover(null)
                  requestUpdate()
                }
              }
            }
          }
        }
      }
      ul("dropdown-menu") {
        options.forEachIndexed { index, option ->
          li {
            if (index == focusedIndex) {
              classes += "focused"
            }
            if (option == selected) {
              classes += "selected"
            }
            attributes["data-value"] = option.value

            +option.label

            onMouseDownFunction = {
              it.preventDefault()
              it.stopPropagation()
              selected = option
              open = false
              onHover(null)
              requestUpdate()
              onSelect(option)
            }

            onMouseEnterFunction = {
              focusedIndex = index
              onHover(option)
              requestUpdate()
            }
          }
        }
      }

      onMouseLeaveFunction = {
        open = false
        focusedIndex = -1
        onHover(null)
        requestUpdate()
      }
    }
  }
}
